import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useState, useEffect } from "react";
import Header from "../../../../components/Header";
import { setupManagementUrl, customerservice } from "../../../../config";
import Swal from "sweetalert2";
import { loanManagementUrl } from "../../../../config";
import { fetchJson, executeAnyMutation } from "../../../../data/DynamicServiceNew/jsondynamicservice";

const setup_url = setupManagementUrl.uri;

const CommissionForm = (props) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));

  // ---- Customer search state ----
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [filteredCustomer, setFilteredCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(
    props.data
      ? { Id: props.data.CustomerId, Name: props.data.CustomerName }
      : null
  );

  // ---- Product (derived from the selected loan, not user-editable) ----
  const [productName, setProductName] = useState(
    props.data ? props.data.ProductName : ""
  );

  // ---- Loans (dependent on selected customer) ----
  const [loans, setLoans] = useState([]);
  const [loadingLoans, setLoadingLoans] = useState(false);

  // Preload loans if editing an existing commission
  useEffect(() => {
    if (props.isEditing && props.data?.CustomerId) {
      fetchLoansForCustomer(props.data.CustomerId);
    }
  }, []);

  // Once loans load for an existing commission, backfill the product name
  // for display (productId itself is already in initialValues).
  useEffect(() => {
    if (props.isEditing && props.data?.LoanId && loans.length > 0 && !productName) {
      const matchedLoan = loans.find((loan) => loan.Id === props.data.LoanId);
      if (matchedLoan) {
        setProductName(matchedLoan.ProductName || matchedLoan.Product || "");
      }
    }
  }, [loans]);

  const fetchLoansForCustomer = async (customerId) => {
    setLoadingLoans(true);
    try {
      const response = await fetchJson({
        objectName: "loanApplicationByCustomer",
        inputObjectName: "loans",
        params: { customerid: customerId },
        baseUrl: loanManagementUrl.uri,
      });

      const results = JSON.parse(response.results) || [];
      // Only active loans, in case the endpoint doesn't already filter
      const activeLoans = results.filter(
        (loan) => loan.IsActive === true || loan.Status === "Active"
      );
      setLoans(activeLoans);
    } catch (error) {
      console.error("Error fetching loans:", error);
      Swal.fire(
        "Loan Lookup Failed",
        "Could not retrieve active loans for this customer.",
        "error"
      );
    } finally {
      setLoadingLoans(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    setLoadingSearch(true);

    try {
      const mutationdata = { searchParameter: searchTerm };

      const customer = await fetchJson({
        objectName: "searchCustomerForLoanRebooking",
        inputObjectName: "searchCustomerforLoanRebooking",
        params: mutationdata,
        baseUrl: customerservice.uri,
      });

      const result = JSON.parse(customer.results);

      if (!result) {
        Swal.fire("Customer Not Found", "Customer does not exist", "warning");
        setFilteredCustomer(null);
      } else {
        setFilteredCustomer(Array.isArray(result) ? result : [result]);
      }
    } catch (error) {
      console.error("Search error:", error);
      Swal.fire(
        "Search Failed",
        "An error occurred during the search. Please try again later.",
        "error"
      );
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSelectCustomer = (customer, setFieldValue) => {
    setSelectedCustomer(customer);
    setFilteredCustomer(null);
    setSearchTerm("");
    setFieldValue("customerId", customer.Id);
    setFieldValue("loanId", ""); // reset loan since customer changed
    setFieldValue("productId", ""); // reset product since it's derived from the loan
    setProductName("");
    setLoans([]);
    fetchLoansForCustomer(customer.Id);
  };

  // When a loan is picked, pull its product straight off the loan record
  // and lock it in — the user never chooses a product directly.
  const handleLoanChange = (e, setFieldValue) => {
    const loanId = e.target.value;
    setFieldValue("loanId", loanId);

    const selectedLoan = loans.find((loan) => loan.Id === loanId);
    if (selectedLoan) {
      setFieldValue("productId", selectedLoan.ProductId ?? "");
      setProductName(selectedLoan.ProductName || selectedLoan.Product || "");
    } else {
      setFieldValue("productId", "");
      setProductName("");
    }
  };

  const initialValues = {
    customerId: props.data ? props.data.CustomerId : "",
    productId: props.data ? props.data.ProductId : "",
    loanId: props.data ? props.data.LoanId : "",
    commissionPercentage: props.data ? props.data.CommissionPercentage : "",
    commissionAmount: props.data ? props.data.CommissionAmount : "",
  };

  const validationSchema = yup.object().shape({
    customerId: yup.string().required("Customer is required"),
    productId: yup.string().required("Product is required"),
    loanId: yup.string().required("Loan is required"),
    commissionPercentage: yup
      .number()
      .typeError("Commission percentage must be a number")
      .min(0, "Cannot be negative")
      .max(100, "Cannot exceed 100%")
      .required("Commission percentage is required"),
    commissionAmount: yup
      .number()
      .typeError("Commission amount must be a number")
      .min(0, "Cannot be negative")
      .required("Commission amount is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        customerId: values.customerId,
        productId: values.productId,
        loanId: values.loanId,
        commissionPercentage: values.commissionPercentage,
        commissionAmount: values.commissionAmount,
        companyId: decodedToken?.CompanyId,
        regionId: decodedToken?.RegionId,
        branchId: decodedToken?.BranchId,
        isActive: true,
      };

      const variables = props.isEditing
        ? { id: props.data.Id, ...payload }
        : payload;

      const response = await executeAnyMutation(
        props.isEditing ? "updateCommission" : "createCommission",
        variables,
        "result",
        false,
        setup_url
      );

      if (response) {
        props.onClose();
        props.onAction();

        await Swal.fire(
          "Success!",
          `Commission has been ${
            props.isEditing ? "updated" : "created"
          } successfully`,
          "success"
        );
      }
    } catch (error) {
      console.error("Error:", error.message);
      Swal.fire(
        "Error",
        "Something went wrong while saving the commission.",
        "error"
      );
    }

    setSubmitting(false);
  };

  return (
    <Box m="20px">
      <Header
        title={props.isEditing ? "Edit Commission" : "Create Commission"}
        subtitle={
          props.isEditing
            ? "Edit an Existing Commission"
            : "Create a New Commission"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box display="grid" gap="30px">
              {/* Customer search */}
              <Box sx={{ gridColumn: "span 2" }}>
                <Box display="flex" gap="10px" alignItems="flex-start">
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Search Customer*"
                    value={
                      selectedCustomer
                        ? selectedCustomer.Name || selectedCustomer.FullName
                        : searchTerm
                    }
                    onChange={(e) => {
                      setSelectedCustomer(null);
                      setFieldValue("customerId", "");
                      handleSearchChange(e);
                    }}
                    error={touched.customerId && !!errors.customerId}
                    helperText={touched.customerId && errors.customerId}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={loadingSearch || !searchTerm.trim()}
                    sx={{ height: "56px", mt: "8px" }}
                  >
                    {loadingSearch ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Search"
                    )}
                  </Button>
                </Box>

                {filteredCustomer && filteredCustomer.length > 0 && (
                  <Paper sx={{ mt: 1, maxHeight: 200, overflowY: "auto" }}>
                    <List dense>
                      {filteredCustomer.map((customer) => (
                        <ListItemButton
                          key={customer.Id}
                          onClick={() =>
                            handleSelectCustomer(customer, setFieldValue)
                          }
                        >
                          <ListItemText
                            primary={customer.Name || customer.FullName}
                            secondary={customer.PhoneNumber || customer.Id}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>

              {/* Loan (depends on selected customer) */}
              <FormControl
                fullWidth
                variant="filled"
                error={touched.loanId && !!errors.loanId}
                disabled={!values.customerId || loadingLoans}
              >
                <InputLabel id="loanId-label">
                  {loadingLoans ? "Loading Loans..." : "Active Loan*"}
                </InputLabel>
                <Select
                  labelId="loanId-label"
                  id="loanId"
                  name="loanId"
                  value={values.loanId}
                  onChange={(e) => handleLoanChange(e, setFieldValue)}
                  onBlur={handleBlur}
                >
                  <MenuItem value="">
                    <em>
                      {values.customerId
                        ? "Select Loan"
                        : "Select a customer first"}
                    </em>
                  </MenuItem>
                  {loans.map((loan) => (
                    <MenuItem key={loan.Id} value={loan.Id}>
                      {loan.LoanNumber || loan.Id} —{" "}
                      {loan.LoanAmount ?? loan.Amount}
                    </MenuItem>
                  ))}
                </Select>
                {touched.loanId && errors.loanId && (
                  <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                    {errors.loanId}
                  </div>
                )}
              </FormControl>

              {/* Product - read-only, set automatically from the selected loan */}
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Product"
                value={productName || (values.loanId ? "" : "Select a loan first")}
                disabled
                InputProps={{ readOnly: true }}
                error={touched.loanId && !!errors.productId}
                helperText={
                  touched.loanId && errors.productId ? errors.productId : ""
                }
              />

              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Commission Percentage*"
                name="commissionPercentage"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.commissionPercentage}
                error={
                  touched.commissionPercentage &&
                  !!errors.commissionPercentage
                }
                helperText={
                  touched.commissionPercentage && errors.commissionPercentage
                }
                inputProps={{ step: "0.01", min: 0, max: 100 }}
              />

              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Commission Amount*"
                name="commissionAmount"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.commissionAmount}
                error={touched.commissionAmount && !!errors.commissionAmount}
                helperText={
                  touched.commissionAmount && errors.commissionAmount
                }
                inputProps={{ step: "0.01", min: 0 }}
              />
            </Box>

            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CommissionForm;